"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { ErrorCode } from "@/generated/error-codes";
import { ApiError } from "@/shared/api-client";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useSignup } from "../api/mutations";
import { postLoginTarget } from "../post-login-target";

export function SignUpForm() {
  const router = useRouter();
  const signup = useSignup();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    signup.mutate(
      { email, password, displayName: displayName || undefined },
      {
        onSuccess: () => {
          // Honor ?redirect (lazily, cacheComponents discipline) — the
          // accept-invite flow signs people up mid-journey.
          router.replace(postLoginTarget(window.location.search));
        },
      },
    );
  };

  const errorMessage = (() => {
    const err = signup.error;
    if (!err) return null;
    if (err instanceof ApiError && err.is(ErrorCode.AUTH_EMAIL_ALREADY_REGISTERED)) {
      return "An account with this email already exists. Try signing in.";
    }
    if (err instanceof ApiError) return err.message;
    return "Could not reach the server. Check your connection and try again.";
  })();

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Ada Lovelace"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 12 characters"
        />
      </div>
      {errorMessage ? (
        <p className="text-sm text-destructive" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <Button type="submit" className="w-full" disabled={signup.isPending}>
        {signup.isPending ? "Creating account…" : "Create account"}
      </Button>
    </form>
  );
}
